import Button from '../../Button'
import { AddCircleIcon24 } from '../../../icons'
import { useCallback, useMemo, useRef, useContext } from 'react'
import { KmZeileForm } from './KmZeileForm'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import MarkAuftraegeAsDoneContext from '../../../contexts/MarkAuftraegeAsDoneContext'

export interface KmZeilenFormProps {
  readOnly: boolean
  testId: string
  activateEmptyFieldWarnings: boolean
}

export function KmZeilenForm({
  readOnly,
  testId,
  activateEmptyFieldWarnings
}: Readonly<KmZeilenFormProps>) {
  const { t } = useTranslation('ui-befehle')

  const formContext = useFormContext()
  const { control, setValue, getValues } = formContext

  const formErrors = useMemo(() => {
    return formContext.formState.errors
  }, [formContext.formState.errors])

  const zeilen = useWatch({
    control,
    name: 'auftrag_8_50.kmZeilen'
  })

  const zeileCount = zeilen?.length ?? 0
  const nextKeyId = useRef(zeileCount)
  const zeileKeys = useRef<number[]>(
    Array.from({ length: Math.max(zeileCount, 1) }, (_, i) => i)
  )

  const addZeile = useCallback(() => {
    if (zeileCount >= 6) {
      return
    }

    const currentZeilen = getValues('auftrag_8_50.kmZeilen') ?? []
    zeileKeys.current.push(++nextKeyId.current)
    setValue('auftrag_8_50.kmZeilen', [...currentZeilen, ''], {
      shouldValidate: false,
      shouldDirty: true
    })
  }, [setValue, getValues, zeileCount])

  const removeLastZeile = useCallback(() => {
    const currentZeilen = getValues('auftrag_8_50.kmZeilen') ?? []
    if (currentZeilen.length <= 1) return

    const newZeilen = currentZeilen.slice(0, -1)
    zeileKeys.current.pop()
    setValue('auftrag_8_50.kmZeilen', newZeilen, {
      shouldValidate: false,
      shouldDirty: true
    })
  }, [setValue, getValues])

  const context = useContext(MarkAuftraegeAsDoneContext)
  const displaySlideToUnlock =
    (context?.isBefehlsnachrichtQuittiert && zeilen.length >= 2) || false

  const zeileComponents = useMemo(() => {
    const zeilenToRender =
      zeilen && Array.isArray(zeilen) && zeilen.length > 0 ? zeilen : ['']

    return zeilenToRender.map((_zeile: string, index: number) => (
      <KmZeileForm
        key={`zeile-${zeileKeys.current[index]}`}
        zeile={index + 1}
        fieldPrefix={`auftrag_8_50.kmZeilen.${index}`}
        readOnly={readOnly}
        displaySlideToUnlock={displaySlideToUnlock}
        testId={`${testId}-zeile-${index}`}
        showRemoveButton={
          !readOnly && index > 0 && index === zeilenToRender.length - 1
        }
        onRemove={() => removeLastZeile()}
        activateEmptyFieldWarnings={activateEmptyFieldWarnings}
        errors={formErrors}
      />
    ))
  }, [
    zeilen,
    readOnly,
    testId,
    displaySlideToUnlock,
    activateEmptyFieldWarnings,
    formErrors,
    removeLastZeile
  ])

  return (
    <>
      {/* Line inputs */}
      <div className="flex flex-col gap-0.5">{zeileComponents}</div>

      {/* Control buttons */}
      {!readOnly && zeileCount < 6 && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="tertiary-plain"
            onClick={addZeile}
            className="flex items-center text-blue-600 min-h-[44px] min-w-[44px] px-1 py-0.5"
            data-testid={`${testId}-add-zeile-button`}
          >
            <AddCircleIcon24 data-testid={`${testId}-add-zeile-button-icon`} />
            <span
              className="ml-0.5 text-sm"
              data-testid={`${testId}-add-zeile-button-text`}
            >
              {t('auftraege.8_50.add_km_zeile')}
            </span>
          </Button>
        </div>
      )}
    </>
  )
}
